import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function ServiceControl() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});
  const [showConfirm, setShowConfirm] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => {
    api.get('/admin/properties')
      .then(res => setProperties(res.data.properties || []))
      .catch(() => toast.error('Could not load properties'))
      .finally(() => setLoading(false));
  }, []);

  const loadProperty = async (property) => {
    setSelectedProperty(property);
    try {
      const [unitRes, logRes] = await Promise.all([
        api.get(`/service/property/${property.id}`),
        api.get(`/service/logs?property_id=${property.id}&limit=20`),
      ]);
      setUnits(unitRes.data.units);
      setLogs(logRes.data.logs);
    } catch {
      toast.error('Could not load property details');
    }
  };

  const toggleUnit = async (unit, action, reason) => {
    setToggling(prev => ({ ...prev, [unit.id]: true }));
    try {
      await api.post(`/service/unit/${unit.id}/toggle`, { action, reason });
      toast.success(`Unit ${unit.unit_number} ${action}`);
      setShowConfirm(null);
      reset();
      await loadProperty(selectedProperty);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update service');
    } finally {
      setToggling(prev => ({ ...prev, [unit.id]: false }));
    }
  };

  const toggleAll = async (action, reason) => {
    try {
      const res = await api.post(`/service/property/${selectedProperty.id}/toggle-all`, { action, reason });
      toast.success(res.data.message);
      setShowConfirm(null);
      await loadProperty(selectedProperty);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update service');
    }
  };

  const onConfirmSubmit = async ({ reason }) => {
    const { unit, action } = showConfirm;
    if (unit === 'all') {
      await toggleAll(action, reason);
    } else {
      await toggleUnit(unit, action, reason);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Service Control</div>
          <div className="page-sub">Turn internet service on or off per unit or per property.</div>

          {/* Confirm modal */}
          {showConfirm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%' }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
                  Confirm: {showConfirm.action} {showConfirm.unit === 'all' ? 'all units' : `unit ${showConfirm.unit.unit_number}`}
                </h3>
                <p style={{ fontSize: 13, color: 'var(--text-sub)', marginBottom: 16 }}>
                  {showConfirm.action === 'deactivated'
                    ? 'This will suspend the tenant\'s internet service. Please provide a reason.'
                    : showConfirm.action === 'activated'
                    ? 'This will restore internet service to this unit.'
                    : 'This will temporarily suspend service.'}
                </p>
                <form onSubmit={handleSubmit(onConfirmSubmit)}>
                  <div className="form-group">
                    <label className="form-label">Reason (required)</label>
                    <textarea className="form-input" rows={3}
                      placeholder="e.g. Non-payment, building maintenance, tenant request..."
                      {...register('reason', { required: true })} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit" className={`btn ${showConfirm.action === 'activated' ? 'btn-green' : 'btn-danger'}`} style={{ flex: 1 }}>
                      Confirm {showConfirm.action}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => { setShowConfirm(null); reset(); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
            {/* Property selector */}
            <div className="card" style={{ marginBottom: 0, alignSelf: 'start' }}>
              <div className="card-header"><div className="card-title">Select property</div></div>
              <div>
                {properties.map((p, i) => (
                  <div key={p.id}
                    onClick={() => loadProperty(p)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: i < properties.length - 1 ? '1px solid var(--border)' : 'none',
                      cursor: 'pointer',
                      background: selectedProperty?.id === p.id ? 'var(--light-bg)' : 'white',
                      borderLeft: selectedProperty?.id === p.id ? '3px solid var(--accent)' : '3px solid transparent',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{p.city}, {p.state} · {p.unit_count} units</div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                      <span style={{ color: 'var(--green)', fontWeight: 600 }}>{p.active_units} active</span>
                      <span style={{ color: 'var(--text-sub)' }}> / {p.unit_count} total</span>
                    </div>
                  </div>
                ))}
                {properties.length === 0 && (
                  <div style={{ padding: 16, color: 'var(--text-sub)', fontSize: 13 }}>No properties found</div>
                )}
              </div>
            </div>

            {/* Unit roster */}
            <div>
              {!selectedProperty ? (
                <div className="card" style={{ padding: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📡</div>
                  <p style={{ color: 'var(--text-sub)' }}>Select a property to manage service</p>
                </div>
              ) : (
                <>
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-header">
                      <div>
                        <div className="card-title">{selectedProperty.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>{selectedProperty.address} · {selectedProperty.city}, {selectedProperty.state}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => setShowConfirm({ unit: 'all', action: 'activated' })}>
                          ✅ Activate all
                        </button>
                        <button className="btn btn-sm btn-danger"
                          onClick={() => setShowConfirm({ unit: 'all', action: 'deactivated' })}>
                          🚫 Deactivate all
                        </button>
                      </div>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr><th>Unit</th><th>Tenant</th><th>Plan</th><th>Status</th><th>Last action</th><th>Control</th></tr>
                        </thead>
                        <tbody>
                          {units.map(u => (
                            <tr key={u.id}>
                              <td style={{ fontWeight: 700 }}>{u.unit_number}</td>
                              <td>
                                {u.first_name ? (
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{u.email}</div>
                                  </div>
                                ) : <span style={{ color: 'var(--text-sub)' }}>Vacant</span>}
                              </td>
                              <td style={{ fontSize: 12 }}>{u.plan_name || '—'}</td>
                              <td>
                                <span className={`badge badge-${u.status === 'active' ? 'green' : u.status === 'suspended' ? 'orange' : 'gray'}`}>
                                  {u.status}
                                </span>
                              </td>
                              <td style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                                {u.last_action ? `${u.last_action} ${u.last_action_at ? new Date(u.last_action_at).toLocaleDateString() : ''}` : '—'}
                              </td>
                              <td>
                                {u.status === 'active' ? (
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--orange)', borderColor: '#f5c060' }}
                                      onClick={() => setShowConfirm({ unit: u, action: 'suspended' })}
                                      disabled={toggling[u.id]}>
                                      Suspend
                                    </button>
                                    <button className="btn btn-sm btn-danger"
                                      onClick={() => setShowConfirm({ unit: u, action: 'deactivated' })}
                                      disabled={toggling[u.id]}>
                                      Deactivate
                                    </button>
                                  </div>
                                ) : u.status === 'vacant' ? (
                                  <span style={{ fontSize: 12, color: 'var(--text-sub)' }}>No tenant</span>
                                ) : (
                                  <button className="btn btn-sm btn-green"
                                    onClick={() => setShowConfirm({ unit: u, action: 'activated' })}
                                    disabled={toggling[u.id]}>
                                    Activate
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Activity log */}
                  <div className="card">
                    <div className="card-header"><div className="card-title">Recent service activity</div></div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Unit</th><th>Action</th><th>Reason</th><th>By</th><th>Date</th></tr></thead>
                        <tbody>
                          {logs.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No activity yet</td></tr>}
                          {logs.map(l => (
                            <tr key={l.id}>
                              <td style={{ fontWeight: 700 }}>{l.unit_number}</td>
                              <td>
                                <span className={`badge badge-${l.action === 'activated' ? 'green' : l.action === 'suspended' ? 'orange' : 'red'}`}>
                                  {l.action}
                                </span>
                              </td>
                              <td style={{ fontSize: 12 }}>{l.reason || '—'}</td>
                              <td style={{ fontSize: 12 }}>{l.employee_name || '—'}</td>
                              <td style={{ fontSize: 12 }}>{fmtDate(l.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
